import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getAffiliateCommissions } from '@/modules/Affiliate/services'
import { getCommissionStatusFilterOptions } from '@/modules/Affiliate/constants'
import { Commission } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface CommissionsProps {
    affiliateId: number
}

interface CommissionFilters {
    status: string
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const Commissions: React.FC<CommissionsProps> = ({ affiliateId }) => {
    const { t } = useTranslation('affiliate')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<CommissionFilters>({
        defaults: { page: 1, page_size: 10 },
        filterDefaults: { status: '' },
    })

    const { control } = useForm<CommissionFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_COMMISSIONS,
            affiliateId,
            queryParams,
            filters,
        ] as const,
        queryFn: () =>
            getAffiliateCommissions(affiliateId, {
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                status: filters?.status as string | undefined,
            }),
        enabled: !!affiliateId,
        placeholderData: true,
    })

    const commissions = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    const filterItems: FilterItemConfig[] = useMemo(
        () => [
            {
                name: 'status',
                type: 'select',
                label: t('status'),
                placeholder: t('status'),
                options: getCommissionStatusFilterOptions(t),
            },
        ],
        [t]
    )

    const columns: DataTableColumn<Commission>[] = useMemo(
        () => [
            {
                key: 'commission_id',
                title: t('id'),
                width: 80,
            },
            {
                key: 'invoice_id',
                title: t('invoice_id'),
                width: 100,
                formatter: {
                    type: 'link',
                    pathKey: 'invoiceDetail',
                    pathParams: (value) => ({ id: String(value) }),
                },
            },
            {
                key: 'revenue_amount',
                title: t('revenue'),
                width: 150,
                render: (value) => (
                    <span className="font-semibold text-blue-600">
                        {formatCurrency(value)}
                    </span>
                ),
            },
            {
                key: 'commission_percent',
                title: t('rate'),
                width: 80,
                render: (value) => `${value}%`,
            },
            {
                key: 'commission_amount',
                title: t('commission'),
                width: 150,
                render: (value) => (
                    <span className="font-semibold text-green-600">
                        {formatCurrency(value)}
                    </span>
                ),
            },
            {
                key: 'status',
                title: t('status'),
                width: 120,
                formatter: {
                    // chip formatter wants colorMap: { value: { label, color } } — there is no
                    // 'status' formatter type nor a separate labelMap (type:'status' fell through
                    // to the default renderer, showing the raw status string).
                    type: 'chip',
                    colorMap: {
                        pending: { label: t('pending'), color: 'warning' },
                        confirmed: { label: t('confirmed'), color: 'success' },
                        refunded: { label: t('refunded'), color: 'error' },
                    },
                },
            },
            {
                key: 'customer_id',
                title: t('customer_id'),
                width: 100,
                formatter: {
                    type: 'link',
                    pathKey: 'customerDetail',
                    pathParams: (value) => ({ id: String(value) }),
                },
            },
            {
                key: 'created_at',
                title: t('created_at'),
                width: 160,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
            {
                key: 'confirmed_at',
                title: t('confirmed_at'),
                width: 160,
                render: (value) =>
                    value ? formatFromUTC(value, 'DD/MM/YYYY HH:mm') : '-',
            },
        ],
        [t]
    )

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <Paper className="space-y-2 my-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />
            {stateComponent || (
                <DataTableCustomized<Commission>
                    columns={columns}
                    rows={commissions}
                    rowKey={(row) => row.commission_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_affiliate'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'affiliate-commissions',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default Commissions
